import { useState } from "react";
import "@/App.css";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChevronLeft, ChevronRight, X, MessageCircle, Check, MapPin, Building, Car, Waves, Dumbbell, PartyPopper, Shield, Zap, Flame } from "lucide-react";
import * as React from "react";

// Use Radix Dialog directly
const Dialog = DialogPrimitive.Root;

// Custom DialogOverlay without default styling issues
const CustomDialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/90 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
));
CustomDialogOverlay.displayName = "CustomDialogOverlay";

// Custom DialogContent without default close button
const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <CustomDialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full translate-x-[-50%] translate-y-[-50%] duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        className
      )}
      {...props}>
      {children}
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
));
DialogContent.displayName = "DialogContent";

// Custom DialogTitle for accessibility
const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
));
DialogTitle.displayName = "DialogTitle";

// Custom DialogDescription for accessibility
const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = "DialogDescription";

// WhatsApp Configuration
const WHATSAPP_NUMBER = "5511967941234";
const WHATSAPP_MESSAGE = encodeURIComponent("Olá, vi o site do apartamento na Rua Bartira, 35 e gostaria de mais informações.");
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`;

// All property images - reordered as requested: 9,8,10,6,7,11,12,13,14,15,16,17,18,27,26,22,21,20,28,25,23,5,4,3,2,1
const GALLERY_IMAGES = [
  { url: "https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/6rxoncpa_AP._JU_CAIO-9.jpg", alt: "Vista do apartamento 9" },
  { url: "https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/9xql1kps_AP._JU_CAIO-8.jpg", alt: "Vista do apartamento 8" },
  { url: "https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/fpsg8vwq_AP._JU_CAIO-10.jpg", alt: "Vista do apartamento 10" },
  { url: "https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/p0mzuy5r_AP._JU_CAIO-6.jpg", alt: "Vista do apartamento 6" },
  { url: "https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/6vw23wsa_AP._JU_CAIO-7.jpg", alt: "Vista do apartamento 7" },
  { url: "https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/gidfcne5_AP._JU_CAIO-11.jpg", alt: "Vista do apartamento 11" },
  { url: "https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/b70qb1l8_AP._JU_CAIO-12.jpg", alt: "Vista do apartamento 12" },
  { url: "https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/sxvow24k_AP._JU_CAIO-13.jpg", alt: "Vista do apartamento 13" },
  { url: "https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/jsq7gobh_AP._JU_CAIO-14.jpg", alt: "Vista do apartamento 14" },
  { url: "https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/39fqumxf_AP._JU_CAIO-15.jpg", alt: "Vista do apartamento 15" },
  { url: "https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/alryuj78_AP._JU_CAIO-16.jpg", alt: "Vista do apartamento 16" },
  { url: "https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/ixfa76r1_AP._JU_CAIO-17.jpg", alt: "Vista do apartamento 17" },
  { url: "https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/2u90unh1_AP._JU_CAIO-18.jpg", alt: "Vista do apartamento 18" },
  { url: "https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/u8beb0nj_AP._JU_CAIO-27.jpg", alt: "Vista do apartamento 27" },
  { url: "https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/w6g5mew9_AP._JU_CAIO-26.jpg", alt: "Vista do apartamento 26" },
  { url: "https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/5fe16lhz_AP._JU_CAIO-22.jpg", alt: "Vista do apartamento 22" },
  { url: "https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/fy32q2zf_AP._JU_CAIO-21.jpg", alt: "Vista do apartamento 21" },
  { url: "https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/rwlqwhk6_AP._JU_CAIO-20.jpg", alt: "Vista do apartamento 20" },
  { url: "https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/zqb28t57_AP._JU_CAIO-28.jpg", alt: "Vista do apartamento 28" },
  { url: "https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/9v3biwq6_AP._JU_CAIO-25.jpg", alt: "Vista do apartamento 25" },
  { url: "https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/zsy98vva_AP._JU_CAIO-23.jpg", alt: "Vista do apartamento 23" },
  { url: "https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/obw9azk2_AP._JU_CAIO-5.jpg", alt: "Vista do apartamento 5" },
  { url: "https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/qubsogyg_AP._JU_CAIO-4.jpg", alt: "Vista do apartamento 4" },
  { url: "https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/114qsk2b_AP._JU_CAIO-3.jpg", alt: "Vista do apartamento 3" },
  { url: "https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/mfg2hx6d_AP._JU_CAIO-2.jpg", alt: "Vista do apartamento 2" },
  { url: "https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/ic4yfj3i_AP._JU_CAIO-1.jpg", alt: "Vista do apartamento 1" },
];

// Floating WhatsApp Button
const WhatsAppFloat = () => (
  <a
    href={WHATSAPP_LINK}
    target="_blank"
    rel="noopener noreferrer"
    className="whatsapp-float animate-pulse-soft"
    data-testid="whatsapp-float-btn"
    aria-label="Contato via WhatsApp"
  >
    <MessageCircle className="w-7 h-7 text-white" />
  </a>
);

// Header Component
const Header = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-40 glass border-b border-[#D5D0CB]/50" data-testid="header">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <button 
            onClick={() => scrollToSection('hero')}
            className="text-[#1A1B18] font-medium tracking-tight text-sm md:text-base"
            data-testid="logo-btn"
          >
            RUA BARTIRA 35
          </button>
          
          <nav className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('galeria')} className="text-[#4A4D45] hover:text-[#1A1B18] text-sm transition-colors">
              Galeria
            </button>
            <button onClick={() => scrollToSection('imovel')} className="text-[#4A4D45] hover:text-[#1A1B18] text-sm transition-colors">
              O Imóvel
            </button>
            <button onClick={() => scrollToSection('bairro')} className="text-[#4A4D45] hover:text-[#1A1B18] text-sm transition-colors">
              O Bairro
            </button>
          </nav>

          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-btn"
          >
            <Button className="bg-[#556B2F] hover:bg-[#435525] text-white text-sm px-4 py-2 rounded-full">
              Falar no WhatsApp
            </Button>
          </a>
        </div>
      </div>
    </header>
  );
};

// Hero image - using AP._JU_CAIO-7.jpg (user's photo)
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/6vw23wsa_AP._JU_CAIO-7.jpg";

// Neighborhood image - user's night view photo of São Paulo
const NEIGHBORHOOD_IMAGE = "https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/cpvmgz0d_PHOTO-2026-03-31-11-01-26.jpg";

// Hero Section - Using design agent's hero image
const HeroSection = () => (
  <section
    id="hero"
    className="hero-section"
    style={{ backgroundImage: `url(${HERO_IMAGE})` }}
    data-testid="hero-section"
  >
    <div className="hero-overlay" />
    
    <div className="relative z-10 min-h-[100dvh] flex flex-col justify-end pb-8 sm:pb-16 md:pb-24 pt-20 sm:pt-24 px-4 sm:px-6 md:px-12 max-w-7xl mx-auto">
      <div className="animate-fade-in-up">
        <p className="text-xs uppercase tracking-[0.2em] text-white/80 mb-3 sm:mb-4 font-semibold">
          Perdizes, São Paulo
        </p>
        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-white leading-tight tracking-tight max-w-4xl mb-4 sm:mb-6">
          Vista aberta, planta generosa e localização estratégica
        </h1>
        <p className="text-sm sm:text-base md:text-lg text-white/90 max-w-2xl mb-5 sm:mb-8 font-light leading-relaxed">
          Um imóvel que combina espaço, conforto e potencial de valorização em uma região em transformação
        </p>
        
        <a
          href={WHATSAPP_LINK}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="hero-cta-btn"
        >
          <Button className="bg-[#556B2F] hover:bg-[#435525] text-white text-sm sm:text-base px-6 sm:px-8 py-4 sm:py-6 rounded-full inline-flex items-center gap-2 sm:gap-3 w-full sm:w-auto justify-center">
            <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
            Solicitar detalhes pelo WhatsApp
          </Button>
        </a>
      </div>

      {/* Stats Overlay */}
      <div className="stats-card mt-5 sm:mt-12 rounded-xl sm:rounded-2xl px-3 sm:px-6 py-3 sm:py-5 w-full sm:w-fit animate-fade-in-up animation-delay-400">
        <div className="grid grid-cols-4 gap-2 sm:flex sm:flex-wrap sm:justify-center sm:gap-4 md:gap-10">
          <div className="text-center">
            <p className="metric-number text-xl sm:text-2xl md:text-4xl text-[#1A1B18]">235</p>
            <p className="text-[9px] sm:text-xs text-[#4A4D45] uppercase tracking-wider mt-0.5 sm:mt-1">m²</p>
          </div>
          <div className="text-center">
            <p className="metric-number text-xl sm:text-2xl md:text-4xl text-[#1A1B18]">4</p>
            <p className="text-[9px] sm:text-xs text-[#4A4D45] uppercase tracking-wider mt-0.5 sm:mt-1">Suítes</p>
          </div>
          <div className="text-center">
            <p className="metric-number text-xl sm:text-2xl md:text-4xl text-[#1A1B18]">4</p>
            <p className="text-[9px] sm:text-xs text-[#4A4D45] uppercase tracking-wider mt-0.5 sm:mt-1">Vagas</p>
          </div>
          <div className="text-center">
            <p className="metric-number text-xl sm:text-2xl md:text-4xl text-[#556B2F]">5.2M</p>
            <p className="text-[9px] sm:text-xs text-[#4A4D45] uppercase tracking-wider mt-0.5 sm:mt-1">R$</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Gallery Section
const GallerySection = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % GALLERY_IMAGES.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length);
  };

  return (
    <section id="galeria" className="py-16 sm:py-24 md:py-32 bg-soft-cream" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        <div className="mb-8 sm:mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#556B2F] font-semibold mb-2 sm:mb-3">
            Galeria
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl tracking-tight text-[#1A1B18]">
            Conheça cada ambiente
          </h2>
        </div>

        {/* Bento Grid */}
        <div className="bento-grid">
          {GALLERY_IMAGES.slice(0, 4).map((img, index) => (
            <div
              key={index}
              className="bento-item overflow-hidden rounded-xl cursor-pointer"
              onClick={() => { setCurrentIndex(index); setIsOpen(true); }}
              data-testid={`gallery-image-${index}`}
            >
              <img
                src={img.url}
                alt={img.alt}
                className="w-full h-full object-cover image-hover"
                loading="lazy"
              />
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <Button
            onClick={() => setIsOpen(true)}
            variant="outline"
            className="border-[#556B2F] text-[#556B2F] hover:bg-[#556B2F] hover:text-white px-8 py-6 rounded-full text-base"
            data-testid="view-all-photos-btn"
          >
            Ver todas as {GALLERY_IMAGES.length} fotos
          </Button>
        </div>

        {/* Full Gallery Dialog */}
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-[#1A1B18] border-none">
            <DialogTitle className="sr-only">Galeria de fotos do apartamento</DialogTitle>
            <DialogDescription className="sr-only">
              Navegue pelas {GALLERY_IMAGES.length} fotos do apartamento
            </DialogDescription>
            
            <div className="relative w-full h-[90vh] flex items-center justify-center">
              {/* Close Button */}
              <button
                onClick={() => setIsOpen(false)}
                className="absolute top-4 right-4 z-[60] w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                data-testid="close-gallery-btn"
              >
                <X className="w-5 h-5 text-white" />
              </button>

              {/* Navigation */}
              <button
                onClick={prevImage}
                className="absolute left-4 z-50 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                data-testid="prev-image-btn"
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>

              <button
                onClick={nextImage}
                className="absolute right-4 z-50 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                data-testid="next-image-btn"
              >
                <ChevronRight className="w-6 h-6 text-white" />
              </button>

              {/* Main Image */}
              <img
                src={GALLERY_IMAGES[currentIndex].url}
                alt={GALLERY_IMAGES[currentIndex].alt}
                className="max-w-full max-h-[80vh] object-contain"
              />

              {/* Counter */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm">
                {currentIndex + 1} / {GALLERY_IMAGES.length}
              </div>

              {/* Thumbnails */}
              <div className="absolute bottom-16 left-1/2 -translate-x-1/2 w-full max-w-2xl px-4">
                <ScrollArea className="w-full">
                  <div className="flex gap-2 justify-center">
                    {GALLERY_IMAGES.map((img, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentIndex(index)}
                        className={`w-16 h-12 rounded overflow-hidden flex-shrink-0 transition-opacity ${
                          index === currentIndex ? 'opacity-100 ring-2 ring-white' : 'opacity-50 hover:opacity-75'
                        }`}
                      >
                        <img
                          src={img.url}
                          alt={img.alt}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

// About Property Section
const AboutPropertySection = () => (
  <section id="imovel" className="py-16 sm:py-24 md:py-32 bg-warm-stone" data-testid="about-property-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-20 items-center">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#556B2F] font-semibold mb-2 sm:mb-3">
            Sobre o Imóvel
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl tracking-tight text-[#1A1B18] mb-4 sm:mb-6">
            Espaço, conforto e sofisticação
          </h2>
          
          <div className="bg-[#556B2F] text-white px-4 py-2 rounded-full inline-block mb-6 sm:mb-8">
            <p className="text-sm sm:text-base font-medium">Pronto para morar! Mobília e decoração inclusa</p>
          </div>
          
          <div className="grid grid-cols-2 gap-x-6 gap-y-8 sm:gap-6 mb-8 sm:mb-10">
            <div>
              <p className="metric-number text-4xl sm:text-5xl md:text-6xl text-[#1A1B18]">235</p>
              <p className="text-[#4A4D45] text-xs sm:text-sm mt-1">metros quadrados</p>
            </div>
            <div>
              <p className="metric-number text-4xl sm:text-5xl md:text-6xl text-[#1A1B18]">4</p>
              <p className="text-[#4A4D45] text-xs sm:text-sm mt-1">suítes</p>
            </div>
            <div>
              <p className="metric-number text-4xl sm:text-5xl md:text-6xl text-[#1A1B18]">4</p>
              <p className="text-[#4A4D45] text-xs sm:text-sm mt-1">vagas de garagem</p>
            </div>
            <div>
              <p className="metric-number text-4xl sm:text-5xl md:text-6xl text-[#556B2F]">5.2</p>
              <p className="text-[#4A4D45] text-xs sm:text-sm mt-1">milhões (R$)</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-x-6 gap-y-4 mb-8 sm:mb-10 p-4 bg-white/50 rounded-xl">
            <div>
              <p className="text-[#4A4D45] text-xs uppercase tracking-wider mb-1">IPTU anual</p>
              <p className="metric-number text-2xl sm:text-3xl text-[#1A1B18]">R$ 32.800</p>
            </div>
            <div>
              <p className="text-[#4A4D45] text-xs uppercase tracking-wider mb-1">Condomínio</p>
              <p className="metric-number text-2xl sm:text-3xl text-[#1A1B18]">R$ 3.740</p>
            </div>
          </div>

          <p className="text-[#4A4D45] text-base sm:text-lg leading-relaxed mb-6 sm:mb-8">
            Apartamento amplo com excelente iluminação natural, ventilação cruzada e vista privilegiada. 
            Planta bem distribuída que proporciona privacidade e integração entre os ambientes.
          </p>

          <ul className="premium-list space-y-2 sm:space-y-3 text-[#4A4D45] text-sm sm:text-base">
            <li>Vista aberta e permanente</li>
            <li>Ambientes amplos e bem iluminados</li>
            <li>Acabamentos de alto padrão</li>
            <li>Vaga com tomada para carro elétrico</li>
          </ul>
        </div>

        <div className="rounded-2xl overflow-hidden">
          <img
            src="https://customer-assets.emergentagent.com/job_apartamento-bartira/artifacts/9xql1kps_AP._JU_CAIO-8.jpg"
            alt="Interior do apartamento"
            className="w-full h-[300px] sm:h-[400px] lg:h-[500px] object-cover image-hover"
            loading="lazy"
          />
        </div>
      </div>
    </div>
  </section>
);

// Neighborhood Section
const NeighborhoodSection = () => (
  <section id="bairro" className="py-16 sm:py-24 md:py-32 bg-soft-cream" data-testid="neighborhood-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-20 items-center">
        <div className="order-2 lg:order-1 rounded-2xl overflow-hidden">
          <img
            src={NEIGHBORHOOD_IMAGE}
            alt="Vista de São Paulo"
            className="w-full h-[250px] sm:h-[350px] lg:h-[500px] object-cover image-hover"
            loading="lazy"
          />
        </div>

        <div className="order-1 lg:order-2">
          <p className="text-xs uppercase tracking-[0.2em] text-[#556B2F] font-semibold mb-2 sm:mb-3">
            Sobre o Bairro
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl tracking-tight text-[#1A1B18] mb-6 sm:mb-8">
            Perdizes, uma região consolidada
          </h2>
          
          <p className="text-[#4A4D45] text-base sm:text-lg leading-relaxed mb-6 sm:mb-8">
            Inserido em uma das regiões mais consolidadas de São Paulo, o entorno da Rua Bartira 
            oferece uma combinação rara entre tranquilidade residencial e infraestrutura completa.
          </p>

          <ul className="space-y-3 sm:space-y-4 mb-8 sm:mb-10">
            {[
              { icon: Building, text: "A poucos minutos da PUC-SP e FAAP" },
              { icon: MapPin, text: "Teatro TUCA, referência cultural da cidade" },
              { icon: Car, text: "Futuras estações de metrô: Estação PUC e FAAP" },
              { icon: Check, text: "Ampla oferta de comércio e gastronomia" },
            ].map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#556B2F]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <item.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#556B2F]" />
                </div>
                <span className="text-[#4A4D45] text-sm sm:text-base">{item.text}</span>
              </li>
            ))}
          </ul>

          <p className="text-[#4A4D45] text-sm sm:text-base leading-relaxed">
            A mobilidade tende a se valorizar ainda mais com a implantação das futuras estações de metrô, 
            ampliando o acesso e conectividade da região.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// Differentials Section
const DifferentialsSection = () => {
  const differentials = [
    { icon: Waves, title: "Piscina", desc: "Área comum com piscina para lazer" },
    { icon: Dumbbell, title: "Academia", desc: "Espaço fitness completo" },
    { icon: PartyPopper, title: "Salão de Festa", desc: "Ambiente para celebrações" },
    { icon: Shield, title: "Portaria 24h", desc: "Portaria remota com segurança" },
    { icon: Zap, title: "Carro Elétrico", desc: "Vaga com tomada para EV" },
    { icon: Flame, title: "Sauna Seca", desc: "Relaxamento e bem-estar" },
    { icon: MapPin, title: "Localização", desc: "Região em valorização" },
  ];

  return (
    <section className="py-16 sm:py-24 md:py-32 bg-warm-stone" data-testid="differentials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        <div className="text-center mb-10 sm:mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-[#556B2F] font-semibold mb-2 sm:mb-3">
            Diferenciais
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl tracking-tight text-[#1A1B18]">
            O que torna este imóvel especial
          </h2>
        </div>

        <div className="diff-grid">
          {differentials.map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-8 border border-[#D5D0CB]/50 hover:shadow-lg transition-shadow"
              data-testid={`differential-${index}`}
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#556B2F]/10 flex items-center justify-center mb-4 sm:mb-5">
                <item.icon className="w-6 h-6 sm:w-7 sm:h-7 text-[#556B2F]" />
              </div>
              <h3 className="text-lg sm:text-xl font-medium text-[#1A1B18] mb-1 sm:mb-2">{item.title}</h3>
              <p className="text-[#4A4D45] text-sm sm:text-base">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = ({ variant = "light" }) => {
  const isLight = variant === "light";
  
  return (
    <section 
      className={`py-14 sm:py-20 md:py-28 ${isLight ? 'bg-soft-cream' : 'bg-[#1A1B18]'}`}
      data-testid={`cta-section-${variant}`}
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-12 text-center">
        <h2 className={`text-2xl sm:text-3xl md:text-4xl tracking-tight mb-5 sm:mb-6 ${isLight ? 'text-[#1A1B18]' : 'text-white'}`}>
          {variant === "mid" 
            ? "Gostaria de conhecer o apartamento pessoalmente?"
            : "Se você busca um imóvel com boa distribuição, vista aberta e localização consolidada, este apartamento merece ser visitado."
          }
        </h2>
        
        <a
          href={WHATSAPP_LINK}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`cta-whatsapp-btn-${variant}`}
        >
          <Button className="bg-[#556B2F] hover:bg-[#435525] text-white text-sm sm:text-base px-6 sm:px-8 py-4 sm:py-6 rounded-full inline-flex items-center gap-2 sm:gap-3 w-full sm:w-auto justify-center">
            <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
            Solicitar detalhes pelo WhatsApp
          </Button>
        </a>
      </div>
    </section>
  );
};

// Footer
const Footer = () => (
  <footer className="py-6 sm:py-8 bg-[#1A1B18] border-t border-white/10" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 text-center">
      <p className="text-white/60 text-xs sm:text-sm">
        Rua Bartira, 35 · Perdizes · São Paulo
      </p>
    </div>
  </footer>
);

// Main App Component
function App() {
  return (
    <div className="App" data-testid="app-container">
      <Header />
      <main>
        <HeroSection />
        <GallerySection />
        <AboutPropertySection />
        <CTASection variant="mid" />
        <NeighborhoodSection />
        <DifferentialsSection />
        <CTASection variant="final" />
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}

export default App;
